import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SavedAnsulCommissioning, PrintLocale } from '../types';
import { useSettings } from '../contexts/SettingsContext';
import { companyPresets, printLanguages } from '../config';
import { formatDate } from '../utils';

interface Props {
    report: SavedAnsulCommissioning;
    onClose: () => void;
}

const AnsulCommissioningPrintView: React.FC<Props> = ({ report, onClose }) => {
    const { t: tUI, i18n } = useTranslation();
    const { settings } = useSettings();

    const [printLanguage, setPrintLanguage] = useState<PrintLocale>(
        report.settings?.printLanguage || settings.printLanguage || 'fi'
    );

    const t = i18n.getFixedT(printLanguage);

    // Map print language to country code
    const langToCountry: Record<string, keyof typeof companyPresets> = {
        fi: 'fi',
        sv: 'se',
        no: 'no',
        da: 'dk',
        pl: 'pl',
        nl: 'nl',
        de: 'de',
        fr: 'fr',
        en: 'gb',
    };

    const company = companyPresets[langToCountry[printLanguage]] || companyPresets[settings.companyCountry];

    const handlePrint = () => {
        window.print();
    };

    const { formData, checklist } = report;

    // Get gas shutoff type label
    const getGasShutoffLabel = () => {
        const labels: Record<string, string> = {
            mechanical: t('ansulCommissioning.gasShutoff.mechanical'),
            electrical: t('ansulCommissioning.gasShutoff.electrical'),
            none: t('ansulCommissioning.gasShutoff.none'),
            automation: t('ansulCommissioning.gasShutoff.automation')
        };
        return labels[formData.gasShutoffType] || formData.gasShutoffType;
    };

    return (
        <div className="print-view">
            {/* Print Controls - Hidden when printing */}
            <div className="print-controls no-print">
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <button className="btn btn-secondary" onClick={onClose}>
                        â† {tUI('common.back')}
                    </button>

                    <select
                        value={printLanguage}
                        onChange={(e) => setPrintLanguage(e.target.value as PrintLocale)}
                        className="form-select"
                        style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd' }}
                    >
                        {printLanguages.map(lang => (
                            <option key={lang.code} value={lang.code}>
                                {lang.nativeName}
                            </option>
                        ))}
                    </select>
                </div>

                <button className="btn btn-primary" onClick={handlePrint}>
                    ðŸ–¨ï¸ {tUI('common.printPdf')}
                </button>
                <span className="print-tip" style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: '12px' }}>
                    ðŸ’¡ {t('print.tip.saveasPdf')}
                </span>
            </div>

            {/* Print Document */}
            <div className="print-document ansul-commissioning-document">
                {/* Header */}
                <header className="print-header">
                    <div className="print-header-left">
                        <img src="/halton-logo.png" alt="Halton" className="print-logo" style={{ height: '32px', marginBottom: '8px' }} />
                        <div className="company-info">
                            {company.name}<br />
                            {company.address}<br />
                            {company.postalCode} {company.city}<br />
                            {t('print.company.vat')}: {company.vatId}
                        </div>
                    </div>

                    <div className="print-header-right">
                        <h1 className="document-title">
                            {t('ansulCommissioning.print.title')}
                        </h1>
                        <div className="document-meta">
                            <div className="meta-row">
                                <span className="meta-label">{t('print.header.documentNumber')}:</span>
                                <span className="meta-value">{report.documentNumber}</span>
                            </div>
                            <div className="meta-row">
                                <span className="meta-label">{t('ansulCommissioning.schedule.commissioningDate')}:</span>
                                <span className="meta-value">{formatDate(formData.commissioningDate, printLanguage)}</span>
                            </div>
                        </div>
                    </div>
                </header>

                {/* Restaurant & System Info */}
                <section className="print-section" style={{ display: 'flex', gap: '30px', marginBottom: '20px' }}>
                    <div style={{ flex: 1 }}>
                        <h2 className="print-section-title">{t('ansulCommissioning.restaurant.title')}</h2>
                        <table className="print-table compact">
                            <tbody>
                                <tr>
                                    <td className="label-cell">{t('ansulCommissioning.restaurant.name')}</td>
                                    <td>{formData.restaurantName}</td>
                                </tr>
                                <tr>
                                    <td className="label-cell">{t('ansulCommissioning.restaurant.address')}</td>
                                    <td>{formData.restaurantAddress}</td>
                                </tr>
                                <tr>
                                    <td className="label-cell">{t('ansulCommissioning.restaurant.contactPerson')}</td>
                                    <td>{formData.contactPerson}</td>
                                </tr>
                                <tr>
                                    <td className="label-cell">{t('ansulCommissioning.restaurant.phone')}</td>
                                    <td>{formData.contactPhone}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div style={{ flex: 1 }}>
                        <h2 className="print-section-title">{t('ansulCommissioning.system.title')}</h2>
                        <table className="print-table compact">
                            <tbody>
                                <tr>
                                    <td className="label-cell">{t('ansulCommissioning.system.type')}</td>
                                    <td>{formData.systemType}</td>
                                </tr>
                                <tr>
                                    <td className="label-cell">{t('ansulCommissioning.system.agent')}</td>
                                    <td>{formData.extinguishingAgent}</td>
                                </tr>
                                <tr>
                                    <td className="label-cell">{t('ansulCommissioning.system.batchNumber')}</td>
                                    <td>{formData.agentBatchNumber}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </section>

                {/* Schedule & Tanks */}
                <section className="print-section" style={{ display: 'flex', gap: '30px', marginBottom: '20px' }}>
                    <div style={{ flex: 1 }}>
                        <h2 className="print-section-title">{t('ansulCommissioning.schedule.title')}</h2>
                        <table className="print-table compact">
                            <tbody>
                                <tr>
                                    <td className="label-cell">{t('ansulCommissioning.schedule.serviceCycle')}</td>
                                    <td>{formData.serviceCycle}</td>
                                </tr>
                                <tr>
                                    <td className="label-cell">{t('ansulCommissioning.schedule.commissioningDate')}</td>
                                    <td>{formatDate(formData.commissioningDate, printLanguage)}</td>
                                </tr>
                                <tr>
                                    <td className="label-cell">{t('ansulCommissioning.schedule.nextServiceDate')}</td>
                                    <td>{formData.nextServiceDate ? formatDate(formData.nextServiceDate, printLanguage) : '-'}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div style={{ flex: 1 }}>
                        <h2 className="print-section-title">{t('ansulCommissioning.tanks.title')}</h2>
                        <table className="print-table compact">
                            <tbody>
                                <tr>
                                    <td className="label-cell">{t('ansulCommissioning.tanks.volume')}</td>
                                    <td>{formData.tankVolume}</td>
                                </tr>
                                <tr>
                                    <td className="label-cell">{t('ansulCommissioning.tanks.filledDate')}</td>
                                    <td>{formData.tankFilledDate ? formatDate(formData.tankFilledDate, printLanguage) : '-'}</td>
                                </tr>
                                <tr>
                                    <td className="label-cell">{t('ansulCommissioning.tanks.fluidExpiry')}</td>
                                    <td>{formData.fluidExpiryDate || '-'}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </section>

                {/* Nitrogen Cylinders */}
                {formData.nitrogenCylinders.length > 0 && (
                    <section className="print-section" style={{ marginBottom: '20px' }}>
                        <h2 className="print-section-title">{t('ansulCommissioning.nitrogen.title')}</h2>
                        <table className="print-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>{t('ansulCommissioning.nitrogen.size')}</th>
                                    <th>{t('ansulCommissioning.nitrogen.stampedDate')}</th>
                                    <th>{t('ansulCommissioning.nitrogen.stampedWeight')}</th>
                                    <th>{t('ansulCommissioning.nitrogen.actualWeight')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {formData.nitrogenCylinders.map((cyl, idx) => (
                                    <tr key={cyl.id}>
                                        <td>{idx + 1}</td>
                                        <td>{cyl.size === 'small' ? t('ansulCommissioning.nitrogen.sizeSmall') : t('ansulCommissioning.nitrogen.sizeLarge')}</td>
                                        <td>{cyl.stampedDate}</td>
                                        <td>{cyl.stampedWeight} g</td>
                                        <td>{cyl.actualWeight} g</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {formData.cylinderRenewalDate && (
                            <p style={{ marginTop: '8px', fontSize: '12px' }}>
                                <strong>{t('ansulCommissioning.nitrogen.renewalDate')}:</strong> {formData.cylinderRenewalDate}
                            </p>
                        )}
                    </section>
                )}

                {/* Manual Actuators & Gas Shutoff */}
                <section className="print-section" style={{ display: 'flex', gap: '30px', marginBottom: '20px' }}>
                    {formData.manualActuators.length > 0 && (
                        <div style={{ flex: 1 }}>
                            <h2 className="print-section-title">{t('ansulCommissioning.actuators.title')}</h2>
                            <table className="print-table compact">
                                <tbody>
                                    {formData.manualActuators.map((act, idx) => (
                                        <tr key={act.id}>
                                            <td style={{ width: '30px' }}>{idx + 1}.</td>
                                            <td>{act.location}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <div style={{ flex: 1 }}>
                        <h2 className="print-section-title">{t('ansulCommissioning.gasShutoff.title')}</h2>
                        <table className="print-table compact">
                            <tbody>
                                <tr>
                                    <td className="label-cell">{t('ansulCommissioning.gasShutoff.type')}</td>
                                    <td>{getGasShutoffLabel()}</td>
                                </tr>
                                {formData.gasShutoffNotes && (
                                    <tr>
                                        <td className="label-cell">{t('ansulCommissioning.gasShutoff.notes')}</td>
                                        <td>{formData.gasShutoffNotes}</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>

                {/* Detectors */}
                {formData.detectors.length > 0 && (
                    <section className="print-section" style={{ marginBottom: '20px' }}>
                        <h2 className="print-section-title">{t('ansulCommissioning.detectors.title')}</h2>
                        <table className="print-table">
                            <thead>
                                <tr>
                                    <th>{t('ansulCommissioning.detectors.color')}</th>
                                    <th>{t('ansulCommissioning.detectors.count')}</th>
                                    <th>{t('ansulCommissioning.detectors.location')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {formData.detectors.map((det) => (
                                    <tr key={det.id}>
                                        <td>{t(`ansulCommissioning.detectors.colors.${det.color}`)}</td>
                                        <td>{det.count} kpl</td>
                                        <td>{det.location}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {formData.detectorNotes && (
                            <p style={{ marginTop: '8px', fontSize: '12px', fontStyle: 'italic' }}>
                                {formData.detectorNotes}
                            </p>
                        )}
                    </section>
                )}

                {/* Nozzles */}
                {formData.nozzles.length > 0 && (
                    <section className="print-section" style={{ marginBottom: '20px' }}>
                        <h2 className="print-section-title">{t('ansulCommissioning.nozzles.title')}</h2>
                        <table className="print-table">
                            <thead>
                                <tr>
                                    <th>{t('ansulCommissioning.nozzles.type')}</th>
                                    <th>{t('ansulCommissioning.nozzles.count')}</th>
                                    <th>{t('ansulCommissioning.nozzles.location')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {formData.nozzles.map((noz) => (
                                    <tr key={noz.id}>
                                        <td>{noz.type}</td>
                                        <td>{noz.count} kpl</td>
                                        <td>{noz.location}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {formData.nozzleNotes && (
                            <p style={{ marginTop: '8px', fontSize: '12px', fontStyle: 'italic' }}>
                                {formData.nozzleNotes}
                            </p>
                        )}
                    </section>
                )}

                {/* Checklist */}
                <section className="print-section">
                    <h2 className="print-section-title">{t('ansulCommissioning.checklist.title')}</h2>
                    <table className="print-table">
                        <thead>
                            <tr>
                                <th style={{ width: '5%' }}>#</th>
                                <th style={{ width: '55%' }}>{t('ansulCommissioning.checklist.checkItem')}</th>
                                <th style={{ width: '15%', textAlign: 'center' }}>{t('ansulCommissioning.checklist.status')}</th>
                                <th style={{ width: '25%' }}>{t('ansulCommissioning.checklist.notes')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {checklist.map((item, idx) => (
                                <tr key={item.id}>
                                    <td>{idx + 1}</td>
                                    <td>{t(`ansulCommissioning.checklist.items.${item.labelKey}`)}</td>
                                    <td style={{ textAlign: 'center' }}>
                                        {item.status === 'ok' && <span className="checklist-status status-ok">âœ“ OK</span>}
                                        {item.status === 'nok' && <span className="checklist-status status-nok">âœ— NOK</span>}
                                        {item.status === 'na' && <span className="checklist-status status-na">N/A</span>}
                                        {!item.status && <span className="checklist-status status-empty">â€”</span>}
                                    </td>
                                    <td>{item.notes || '-'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </section>

                {/* Additional Notes */}
                {formData.additionalNotes && (
                    <section className="print-section">
                        <h2 className="print-section-title">{t('ansulCommissioning.additionalNotes')}</h2>
                        <div className="description-box">
                            {formData.additionalNotes}
                        </div>
                    </section>
                )}

                {/* Instructions (Page Break) */}
                <section className="print-section" style={{ pageBreakBefore: 'always' }}>
                    <h2 className="print-section-title">{t('ansulCommissioning.print.ownerInstructions.title')}</h2>
                    <div className="instruction-block instruction-primary">
                        <p style={{ marginBottom: '12px' }}>
                            <strong>{t('ansulCommissioning.print.ownerInstructions.daily')}</strong>
                        </p>
                        <p style={{ marginBottom: '8px' }}>
                            <strong>{t('ansulCommissioning.print.ownerInstructions.monthly')}</strong>
                        </p>
                        <ul style={{ marginLeft: '20px', listStyleType: 'disc' }}>
                            {(t('ansulCommissioning.print.ownerInstructions.monthlyItems', { returnObjects: true }) as string[]).map((item, idx) => (
                                <li key={idx} style={{ marginBottom: '4px' }}>{item}</li>
                            ))}
                        </ul>
                    </div>

                    <h2 className="print-section-title">{t('ansulCommissioning.print.serviceInstructions.title')}</h2>
                    <div className="instruction-block instruction-warning">
                        <p style={{ marginBottom: '12px' }}>
                            <strong>6 kk:</strong> {t('ansulCommissioning.print.serviceInstructions.semiAnnual')}
                        </p>
                        <p>
                            <strong>10 v:</strong> {t('ansulCommissioning.print.serviceInstructions.tenYear')}
                        </p>
                    </div>
                </section>

                {/* Technician Info */}
                <section className="print-section">
                    <h2 className="print-section-title">{t('ansulCommissioning.technician.title')}</h2>
                    <table className="print-table compact" style={{ maxWidth: '400px' }}>
                        <tbody>
                            <tr>
                                <td className="label-cell">{t('ansulCommissioning.technician.company')}</td>
                                <td>{formData.technicianCompany}</td>
                            </tr>
                            <tr>
                                <td className="label-cell">{t('ansulCommissioning.technician.name')}</td>
                                <td>{formData.technicianName}</td>
                            </tr>
                            <tr>
                                <td className="label-cell">{t('ansulCommissioning.technician.phone')}</td>
                                <td>{formData.technicianPhone}</td>
                            </tr>
                            <tr>
                                <td className="label-cell">{t('ansulCommissioning.schedule.nextServiceDate')}</td>
                                <td>{formData.nextServiceDate ? formatDate(formData.nextServiceDate, printLanguage) : '-'}</td>
                            </tr>
                        </tbody>
                    </table>
                </section>

                {/* Signatures */}
                <section className="signature-section">
                    <h3 className="section-title">{t('ansulCommissioning.print.signatures.title')}</h3>
                    <div className="signature-boxes">
                        <div className="signature-box">
                            <div className="signature-box-title">{t('ansulCommissioning.print.signatures.technician')}</div>
                            <div className="signature-line"></div>
                            <div className="signature-label">{t('ansulCommissioning.print.signatures.technician')}</div>
                            <div className="signature-name">{formData.technicianName}</div>
                            <div className="signature-date">
                                <span className="signature-date-label">{t('ansulCommissioning.print.signatures.date')}:</span>
                                <span className="signature-date-line"></span>
                            </div>
                        </div>
                        <div className="signature-box">
                            <div className="signature-box-title">{t('ansulCommissioning.print.signatures.customer')}</div>
                            <div className="signature-line"></div>
                            <div className="signature-label">{t('ansulCommissioning.print.signatures.customer')}</div>
                            <div className="signature-name">{formData.contactPerson}</div>
                            <div className="signature-date">
                                <span className="signature-date-label">{t('ansulCommissioning.print.signatures.date')}:</span>
                                <span className="signature-date-line"></span>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Footer Note */}
                <footer className="print-footer print-footer--centered">
                    <p>{t('ansulCommissioning.print.purpose')}</p>
                </footer>
            </div>
        </div>
    );
};

export default AnsulCommissioningPrintView;


